import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import uuidV4 from 'uuid/v4';
import IconChevronUp from '@spothero/icons/chevron-up';
import Button from '@spothero/ui/button';
import AnimationUtils from '@spothero/utils/animation';
import DOMUtils from '@spothero/utils/dom';
import withConfig from 'config/withConfig';
import SearchMeta from 'search/SearchMeta';
import SegmentUtils from 'utils/segment';
import SHUtils from 'utils/sh-utils';
import GTMUtils from 'utils/gtm';
import ExperimentUtils from 'utils/experiment';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {push} from 'router/router-actions';
import {notify} from 'notification/notification-actions';
import {getSpotsAirportStructuredData} from 'destination/destination-actions';
import SearchAirportControls from 'common/airport/date-pickers/SearchAirportControls';
import AirportHero from './AirportHero';
import AirportStructuredDataCard from './AirportStructuredDataCard';
import ParkingRates from './landing-content/ParkingRates';
import WhyBook from './landing-content/WhyBook';
import CustomerSupport from './landing-content/CustomerSupport';
import TrustedByUsers from './landing-content/TrustedByUsers';
import HowToBook from './landing-content/HowToBook';
import {Helmet} from 'react-helmet-async';

class MidFunnelAirport extends PureComponent {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        siteUrl: PropTypes.string.isRequired,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        city: PropTypes.object,
        hasUserInput: PropTypes.bool,
        loadAirportStructuredDataSpots: PropTypes.func.isRequired,
        airportStructuredData: PropTypes.array,
        airportLowestDailyRate: PropTypes.number,
        sendNotification: PropTypes.func.isRequired,
        pushTo: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this._isAirportShortenedDatepickerEnabled = ExperimentUtils.isFeatureEnabled(
            'airport_datepicker_test'
        );
        this._isNewDesignEnabled = ExperimentUtils.isFeatureEnabled(
            'airport_destination_page_new_design'
        );
        this._showSEOComponent = ExperimentUtils.getFeatureVariable({
            featureName: 'airport_destination_page_new_design',
            variableName: 'seo_component',
            type: 'Boolean',
        });
        this._bodyClasses = SHUtils.determineInitialLoadBodyClasses({
            pageType: 'midfunnel',
            isAirport: true,
        });
    }

    componentDidMount() {
        const {
            loadAirportStructuredDataSpots,
            destination: {airport: {iata_code: iataCode} = {}},
        } = this.props;

        loadAirportStructuredDataSpots(iataCode);

        SegmentUtils.track({
            event: 'Airport Landing',
        });

        GTMUtils.push({
            event: 'pageview',
            page: 'search-airport',
        });

        SHUtils.staticScrollTo();
    }

    _onSearchSubmit = ({starts, ends}) => {
        const {
            pushTo,
            destination: {airport: {iata_code: iataCode} = {}},
        } = this.props;

        pushTo(
            `/search?airport-code=${iataCode}&airport=true&starts=${starts}&ends=${ends}`
        );
    };

    _onViewRatesClick = () => {
        this._scrollToSEODescription();
    };

    _onStructuredDataClick = () => {
        const {isMobile, sendNotification} = this.props;
        const topPos = isMobile
            ? DOMUtils.getDocumentOffset('.SearchAirportControls').top - 52
            : 0;

        AnimationUtils.scrollTo('html', topPos);

        sendNotification({
            id: uuidV4(),
            type: 'info',
            message: 'Please search to check availability.',
        });
    };

    _scrollToSEODescription = () => {
        const airportContentTop = DOMUtils.getDocumentOffset(
            '.MidFunnelAirport-description'
        ).top;
        const topPos = this.props.isMobile
            ? airportContentTop - 52 // - height of nav
            : airportContentTop;

        AnimationUtils.scrollTo('html', topPos);
    };

    render() {
        const {
            isMobile,
            destination,
            city,
            hasUserInput,
            airportStructuredData,
            airportLowestDailyRate,
            siteUrl,
        } = this.props;
        const {relative_url: destinationRelativeUrl} = destination;
        const {
            slug: citySlug,
            phone_number: phoneNumber,
            phone_number_friendly: phoneNumberFriendly,
            weekday_hours: hours,
        } = city;
        const {title: destinationTitle} = destination;
        const landingContentClasses = classNames(
            'MidFunnelAirport-container',
            'MidFunnelAirport-landing-content',
            {'with-search-results': hasUserInput}
        );

        return (
            <div className="MidFunnelAirport">
                <Helmet>
                    <body className={this._bodyClasses} />
                </Helmet>
                <SearchMeta />
                <AirportHero
                    destination={destination}
                    city={city}
                    isMobile={isMobile}
                    isNewDesignEnabled={this._isNewDesignEnabled}
                />
                <div className="MidFunnelAirport-content">
                    <div className="MidFunnelAirport-container MidFunnelAirport-datepicker-container">
                        <SearchAirportControls
                            className={
                                this._isNewDesignEnabled
                                    ? 'SearchAirportControls-shortened-hero'
                                    : ''
                            }
                            isAirportShortenedDatepickerEnabled={
                                this._isAirportShortenedDatepickerEnabled
                            }
                            onSubmit={this._onSearchSubmit}
                        />
                    </div>
                </div>
                {this._isNewDesignEnabled && (
                    <div className={landingContentClasses}>
                        {this._showSEOComponent && (
                            <ParkingRates
                                airportTitle={destinationTitle}
                                lowestDailyRate={airportLowestDailyRate}
                                onViewRatesClick={this._onViewRatesClick}
                            />
                        )}
                        <WhyBook />
                        <CustomerSupport
                            phoneNumber={phoneNumber}
                            phoneNumberFriendly={phoneNumberFriendly}
                            hours={hours}
                        />
                        <TrustedByUsers />
                        <HowToBook />
                    </div>
                )}
                {Boolean(destination.description) && (
                    <div
                        className="MidFunnelAirport-container MidFunnelAirport-description"
                        dangerouslySetInnerHTML={{
                            __html: destination.description,
                        }}
                    />
                )}
                <div className="MidFunnelAirport-container MidFunnelAirport-find-parking-now">
                    <Button
                        color="primary"
                        iconPosition="right"
                        onClick={this._onStructuredDataClick}
                    >
                        Find Parking Now
                        <IconChevronUp />
                    </Button>
                </div>
                {airportStructuredData && airportStructuredData.length > 0 && (
                    <div className="MidFunnelAirport-container MidFunnelAirport-structured-data">
                        <h2>Top Rated Parking Near {destination.title}</h2>
                        <p>
                            Input your dates above to see the full list of
                            parking options available through SpotHero.
                        </p>
                        <ul
                            className="MidFunnelAirport-structured-data-spots"
                            itemScope
                            itemType="http://schema.org/ItemList"
                        >
                            <link
                                itemProp="url"
                                href={`${siteUrl}/${citySlug}/${destinationRelativeUrl}`}
                            />
                            <meta
                                itemProp="numberOfItems"
                                content={airportStructuredData.length}
                            />
                            {airportStructuredData.map((spot, index) => {
                                return (
                                    <li
                                        className="MidFunnelAirport-structured-data-spot-wrapper"
                                        key={spot.facility.id}
                                        itemProp="itemListElement"
                                        itemScope
                                        itemType="http://schema.org/ListItem"
                                    >
                                        <meta
                                            itemProp="position"
                                            content={index + 1}
                                        />
                                        <AirportStructuredDataCard
                                            key={spot.facility.id}
                                            spot={spot}
                                            destinationUrl={`${siteUrl}/${citySlug}/${destinationRelativeUrl}`}
                                            onStructuredDataClick={
                                                this._onStructuredDataClick
                                            }
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: city},
        destination: {
            data: destination,
            airportStructuredData,
            airportLowestDailyRate,
        },
        searchRequest: {starts, ends},
    } = state;

    return {
        destination,
        city,
        airportStructuredData,
        airportLowestDailyRate,
        hasUserInput: Boolean(starts && ends),
    };
};

const mapDispatchToProps = {
    loadAirportStructuredDataSpots: getSpotsAirportStructuredData,
    sendNotification: notify,
    pushTo: push,
};

const enhance = compose(
    withConfig(['isMobile', 'siteUrl']),
    connect(
        mapStateToProps,
        mapDispatchToProps
    )
);

export default enhance(MidFunnelAirport);
